
require("./../../../node_modules/@module-federation/nextjs-mf/src/include-defaults.js");
import dynamic from 'next/dynamic';
import { gql } from '@apollo/client';
import createBffClient from 'api/graphql/bffClient';
import {
  StorePageDocument,
  StorePageQueryVariables,
  StorePageQuery,
  WallOfCardsDocument,
  WallOfCardsQuery,
  WallOfCardsQueryVariables,
  SkuPresentationListDocument,
  SkuPresentationListQuery,
  SkuPresentationListQueryVariables,
  PrezzeePlusStoreBannerDocument,
  PrezzeePlusStoreBannerQuery,
  PrezzeePlusStoreBannerQueryVariables,
} from 'api/graphql/generated-bff';
import { getTokenOptServer } from 'auth/user/AuthUtils.bs';
import { EnvUtil } from 'common/util.bs';
import * as API from 'api/queries/index';
import { getPageContent, getSkuPresentationList } from 'api/graphql/CmsData.bs';
import { modifyStoreContent } from 'api/queries/modifyBlocks';
import getFilterList from 'api/queries/filter';
import logger from 'utils/logger';
import { EMPTY_STRING } from 'common/utils/string';
import { NO_CACHE, PRODUCT_SSR_PAGE_CACHE } from 'common/constants';
import { GetServerSideProps } from 'next';
import { envUtil } from 'common/util';
import chunkArray from 'utils/chunkArray';
import executeBatchedGraphQLRequests from 'utils/executeBatchedGraphQLRequests';
import getSsrAuthHeaders from 'utils/getSsrAuthHeaders';

// @ts-ignore
const StorePage = dynamic(() => import('../../async-pages/store'), { loading: () => <span></span> });

export const getServerSideProps: GetServerSideProps = async ctx => {
  const cookies = ctx.req.headers.cookie;
  const isPreview = !!ctx.query?.preview;
  const language = EnvUtil.getCmsLanguage();
  const config = await API.webConfig();

  const authHeaders = getSsrAuthHeaders(cookies);

  if (!isPreview) {
    ctx.res.setHeader('Cache-Control', PRODUCT_SSR_PAGE_CACHE);
  }

  try {
    if (config.featureLayerBuyingWeb) {
      const client = await createBffClient();
      const bffHeaders = {
        ...(isPreview && { 'x-cms-preview': true }),
        ...authHeaders,
      };

      const wocResponse = await client.query<WallOfCardsQuery, WallOfCardsQueryVariables>({
        query: gql(WallOfCardsDocument),
        variables: {
          codename: envUtil.getStoreCodename(),
        },
        context: {
          headers: bffHeaders,
        },
      });

      const inventory = wocResponse.data?.inventory;
      const store = wocResponse.data?.cms_store;

      // Kontent GraphQL API has a POST request body size limit of 8kb so we have to batch the requests in order to bypass this limit
      // https://kontent.ai/learn/reference/delivery-graphql-api/#a-post-request
      const inventoryCodenames = inventory?.skus.filter(({ cmsId }) => cmsId).map(({ cmsId }) => cmsId) || [];
      const chunkedCodenames = chunkArray(inventoryCodenames, 10);
      const presentationRequests = chunkedCodenames
        .map(async codenames => {
          return await client.query<SkuPresentationListQuery, SkuPresentationListQueryVariables>({
            query: gql(SkuPresentationListDocument),
            variables: {
              language,
              codenames: codenames,
            },
            context: {
              headers: bffHeaders,
            },
          });
        })
        .flat(1);

      const batchedPresentationResponse = await executeBatchedGraphQLRequests<SkuPresentationListQuery>(
        presentationRequests
      );

      const presentationList = batchedPresentationResponse
        .map(({ data }) => data.cms_skuPresentation_All.items)
        .flat(1);

      const storePage = await client.query<StorePageQuery, StorePageQueryVariables>({
        query: gql(StorePageDocument),
        variables: {
          language,
        },
        context: {
          headers: bffHeaders,
        },
      });

      const smartCardContentResponse = await client.query<
        PrezzeePlusStoreBannerQuery,
        PrezzeePlusStoreBannerQueryVariables
      >({
        query: gql(PrezzeePlusStoreBannerDocument),
        variables: { language },
        context: { headers: bffHeaders },
      });

      return {
        props: {
          storePageContentItems: storePage.data?.cms_page.content.contentBlocks.items,
          smartCardContent: smartCardContentResponse.data?.cms_enrichedTextBlock,
          inventory,
          store,
          presentationList,
          config,
        },
      };
    } else {
      const [inv, cat, cont] = await Promise.all([
        API.inventory(getTokenOptServer(cookies)),
        API.categories(),
        getPageContent(language, isPreview, '/store/'),
      ]);
      const codeNames = inv.skus.filter(({ cmsId }: any) => cmsId !== EMPTY_STRING).map(({ cmsId }) => cmsId);
      const [list, resultModified] = await Promise.all([
        getSkuPresentationList(language, isPreview, codeNames),
        modifyStoreContent(JSON.stringify(cont.content), isPreview, language),
      ]);
      const filterList = await getFilterList(language, isPreview);

      return {
        props: {
          inventory: inv || null,
          categories: cat,
          content: resultModified,
          skuList: list ? JSON.stringify(list) : EMPTY_STRING,
          filterList,
          config,
        },
      };
    }
  } catch (error) {
    logger.error({ message: 'Failed to fetch Store Page content', err: error });
    ctx.res.setHeader('location', '/error/');
    ctx.res.setHeader('Cache-Control', NO_CACHE);
    ctx.res.statusCode = 500;
    ctx.res.end();
    return {
      props: {
        inventory: null,
        categories: { results: [] },
        content: null,
        skuList: null,
      },
    };
  }
};

export default StorePage;
